import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikState } from 'formik'
import produce from 'immer'

import { useToastContext } from '../../../../../../../contexts'
import { DeliveryForecastDespatchAdvicePackagingItem } from '../../../../../../../data/models/_generated'
import { getHandleableError } from '../../../../../../../utils/errors/isXiaError'
import { requestCreateDeliveryForecastDespatchAdviceItemPackagingItem } from '../../../../../data/api'
import { useDeliveryForecastDespatchAdviceItemPackagingById } from '../../../../../data/hooks'
import { FormValues } from '../../common'
import { AddForm } from './Add.form'

type Props = {
  despatchAdviceId: string
  itemId: string
  packagingId: string
  label: DeliveryForecastDespatchAdvicePackagingItem['PackagingType']
  isVisible: boolean
  onHide: () => void
}

export const AddData: React.FC<Props> = ({
  despatchAdviceId,
  itemId,
  packagingId,
  label,
  isVisible,
  onHide,
}) => {
  const { t } = useTranslation()
  const { showToast } = useToastContext()

  const { data: itemPackagings } =
    useDeliveryForecastDespatchAdviceItemPackagingById(
      despatchAdviceId,
      itemId,
      packagingId,
    )

  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const handleSubmit = async (
    formData: FormValues,
    resetForm: (
      nextState?: Partial<FormikState<FormValues>> | undefined,
    ) => void,
  ) => {
    try {
      setIsRequestRunning(true)

      const packaging = dtoFromFormData(formData)

      await requestCreateDeliveryForecastDespatchAdviceItemPackagingItem(
        despatchAdviceId,
        itemId,
        packagingId,
        packaging,
      )

      onHide()

      resetForm()

      showToast({
        severity: 'success',
        summary: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.add.messages.success.summary',
        ),
        detail:
          formData.label === 'Single'
            ? t(
                'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.add.messages.success.detailSingle',
                {
                  description: formData.packagingData?.Description,
                  quantity: formData.quantity,
                },
              )
            : t(
                'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.add.messages.success.detailMasterAndHelper',
                {
                  description: formData.packagingData?.Description,
                },
              ),
        closable: true,
      })
    } catch (error) {
      const handleableError = getHandleableError(error)
      const errorMessage = handleableError.message

      // show error toast
      showToast({
        severity: 'error',
        summary: t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.add.dialog.add.messages.failure.summary',
        ),
        detail: errorMessage,
        sticky: true,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const getNewPositionNumber = (): string => {
    const findMaxPosition = (
      items: DeliveryForecastDespatchAdvicePackagingItem[] | undefined,
      currentMax: number,
    ): number => {
      for (const item of items || []) {
        const itemPosition = Number(item.Position)
        if (itemPosition > currentMax) {
          currentMax = itemPosition
        }
        currentMax = findMaxPosition(item.Items, currentMax)
      }
      return currentMax
    }

    const maxPosition = findMaxPosition(itemPackagings?.Items, 0)
    const newPositionNumber = maxPosition + 1

    return newPositionNumber.toString()
  }

  const dtoFromFormData = (
    formData: FormValues,
  ): DeliveryForecastDespatchAdvicePackagingItem => {
    const newPackagingData = produce(formData.packagingData, draft => {
      if (draft) {
        draft.MasterDataId = formData.packagingData?.Id
      }
    })

    return {
      ParentId: formData.selectMaster,
      PackagingType: formData.label!,
      Position: getNewPositionNumber(),
      Label: formData.label,
      PackagingId: formData.packagingData?.PackagingDataId,
      Description: formData.packagingData?.Description,
      HuNumber: formData.huNumber,
      IsOuterPackaging: formData.isOuterPackaging,
      TareWeight: formData.packagingData?.TareWeight?.Value
        ? {
            Value: formData.packagingData?.TareWeight.Value,
            Unit: formData.packagingData?.TareWeight.Unit,
          }
        : undefined,
      PackagingData: newPackagingData!,
      Quantity: {
        Value: formData.quantity,
        Unit: formData.quantityUnit,
      },
      Weight: {
        Value: formData.weight,
        Unit: formData.weightUnit,
      },
      Batches: formData.batches,
    }
  }

  return (
    <AddForm
      handleSubmit={handleSubmit}
      isRequestRunning={isRequestRunning}
      despatchAdviceId={despatchAdviceId}
      itemId={itemId}
      packagingsItems={itemPackagings?.Items}
      label={label}
      isVisible={isVisible}
      onHide={onHide}
    />
  )
}
